import _cloneDeep from 'lodash.clonedeep'
import _isEqual from 'lodash.isequal'
import { formatDate, formatTime } from '@smarttransit/common'
import {
  addOfflineNotificationSetting,
  deleteOfflineNotificationSetting,
  getOfflineNotificationSetting,
  getOfflineNotificationSettings,
  updateOfflineNotificationSetting
} from '@/services/st-settings-service'
import { addAlert } from '@/utilities/helpers'

const defaultProtectedAgencyId = 'STRANSITGHA'
const defaultCurrentConfig = {
  agencyId: '',
  monitorType: 'bus',
  enabled: true,
  offlineThresholdMinutes: 15,
  notificationIntervalMinutes: 30,
  consecutiveMapApiFailures: 4,
  contacts: [],
  terminalCoordinates: []
}

export default {
  name: 'advanced-offline-monitors',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      configs: [],
      apiInProgress: false,
      currentConfig: null,
      originalCurrentConfig: null,
      defaultProtectedAgencyId,
      monitorTypes: [
        { text: 'Bus', value: 'bus' },
        { text: 'EV Charger', value: 'charger' }
      ],
      contactTypes: [
        { text: 'Phone', value: 'phone' },
        { text: 'Email', value: 'email' }
      ]
    }
  },
  computed: {
    isCurrentConfigEdited () {
      return this.currentConfig && !_isEqual(this.originalCurrentConfig, this.currentConfig)
    },
    agencyOptions () {
      const agencies = this.$store.getters.getAgencies || []
      return agencies.map((agency) => (agency.value))
    }
  },
  mounted () {
    this.loadConfigs()
  },
  methods: {
    parseTimeStringToDate (timeLabel) {
      if (!timeLabel || typeof timeLabel !== 'string') {
        return null
      }
      const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(timeLabel.trim())
      if (!match) {
        return null
      }
      const date = new Date(0)
      date.setUTCHours(Number(match[1]), Number(match[2]), 0, 0)
      return date
    },
    formatTimeValueToHHMM (value) {
      if (value instanceof Date && !isNaN(value.getTime())) {
        const hours = String(value.getUTCHours()).padStart(2, '0')
        const minutes = String(value.getUTCMinutes()).padStart(2, '0')
        return `${hours}:${minutes}`
      }
      if (typeof value === 'string') {
        const trimmed = value.trim()
        if (/^([01]\d|2[0-3]):([0-5]\d)$/.test(trimmed)) {
          return trimmed
        }
      }
      return ''
    },
    getAgencyLabel (agencyId) {
      const agencies = this.$store.getters.getAgencies || []
      const found = agencies.find((agency) => (agency.value === agencyId))
      return found ? `${found.text} (${agencyId})` : agencyId
    },
    getMonitorTypeLabel (monitorType) {
      const found = this.monitorTypes.find((type) => (type.value === monitorType))
      return found ? found.text : 'Bus'
    },
    getConfigListLabel (config) {
      if (!config) {
        return ''
      }
      return `${this.getMonitorTypeLabel(config.monitorType)} - ${this.getAgencyLabel(config.agencyId)}`
    },
    isChargerMonitor (config) {
      return config && config.monitorType === 'charger'
    },
    normalizeConfig (config) {
      const contacts = config.data && Array.isArray(config.data.contacts) ? _cloneDeep(config.data.contacts) : []
      const normalizedContacts = contacts.map((contact) => ({
        type: contact && contact.type ? String(contact.type).trim() : 'phone',
        name: contact && contact.name ? String(contact.name).trim() : '',
        value: contact && contact.value ? String(contact.value).trim() : '',
        notifyTimeRanges: Array.isArray(contact && contact.notifyTimeRanges)
          ? contact.notifyTimeRanges
            .filter((range) => (Array.isArray(range) && range.length === 2))
            .map((range) => ([this.parseTimeStringToDate(range[0]), this.parseTimeStringToDate(range[1])]))
          : []
      }))

      return {
        id: config.id,
        agencyId: config.metadata,
        monitorType: config.data && config.data.monitorType === 'charger' ? 'charger' : 'bus',
        enabled: config.data && typeof config.data.enabled === 'boolean' ? config.data.enabled : true,
        offlineThresholdMinutes: config.data && config.data.offlineThresholdMinutes ? Number(config.data.offlineThresholdMinutes) : 10,
        notificationIntervalMinutes: config.data && config.data.notificationIntervalMinutes ? Number(config.data.notificationIntervalMinutes) : 30,
        consecutiveMapApiFailures: config.data && !isNaN(Number(config.data.consecutiveMapApiFailures)) ? Number(config.data.consecutiveMapApiFailures) : 0,
        contacts: normalizedContacts,

        terminalCoordinates: config.data && Array.isArray(config.data.terminalCoordinates)
          ? _cloneDeep(config.data.terminalCoordinates).map((terminal) => ({
            name: terminal && terminal.name ? String(terminal.name).trim() : '',
            latitude: terminal && !isNaN(Number(terminal.latitude)) ? Number(terminal.latitude) : null,
            longitude: terminal && !isNaN(Number(terminal.longitude)) ? Number(terminal.longitude) : null,
            radiusMeters: terminal && !isNaN(Number(terminal.radiusMeters)) ? Number(terminal.radiusMeters) : 100
          }))
          : [],
        dateUpdated: config.dateUpdated
      }
    },
    async loadConfigs () {
      try {
        const configs = await getOfflineNotificationSettings()
        this.configs = (configs || []).map((config) => {
          const normalized = this.normalizeConfig(config)
          normalized.formattedDateUpdated = normalized.dateUpdated ? `${formatDate(normalized.dateUpdated)}, ${formatTime(normalized.dateUpdated)}` : ''
          return normalized
        })
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      }
    },
    async editConfig (id) {
      try {
        const config = await getOfflineNotificationSetting(id)
        this.currentConfig = this.normalizeConfig(config)
        this.originalCurrentConfig = _cloneDeep(this.currentConfig)
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      }
    },
    createConfig () {
      if (this.isCurrentConfigEdited && !confirm('Confirm overwriting the current config')) {
        return
      }
      this.currentConfig = _cloneDeep(defaultCurrentConfig)
      this.originalCurrentConfig = _cloneDeep(this.currentConfig)
    },
    addContact () {
      if (!this.currentConfig) {
        return
      }
      this.currentConfig.contacts.push({
        type: 'phone',
        name: '',
        value: '',
        notifyTimeRanges: []
      })
    },
    addTerminal () {
      if (!this.currentConfig) {
        return
      }
      this.currentConfig.terminalCoordinates.push({
        name: '',
        latitude: null,
        longitude: null,
        radiusMeters: 100
      })
    },
    removeTerminal (index) {
      if (!this.currentConfig || !Array.isArray(this.currentConfig.terminalCoordinates)) {
        return
      }
      this.currentConfig.terminalCoordinates.splice(index, 1)
    },
    addTimeRange (contactIndex) {
      if (!this.currentConfig || !Array.isArray(this.currentConfig.contacts) || !this.currentConfig.contacts[contactIndex]) {
        return
      }
      if (!Array.isArray(this.currentConfig.contacts[contactIndex].notifyTimeRanges)) {
        this.currentConfig.contacts[contactIndex].notifyTimeRanges = []
      }
      this.currentConfig.contacts[contactIndex].notifyTimeRanges.push(['', ''])
    },
    removeTimeRange (contactIndex, rangeIndex) {
      const contact = this.currentConfig && Array.isArray(this.currentConfig.contacts) ? this.currentConfig.contacts[contactIndex] : null
      if (!contact || !Array.isArray(contact.notifyTimeRanges)) {
        return
      }
      contact.notifyTimeRanges.splice(rangeIndex, 1)
    },
    removeContact (index) {
      if (!this.currentConfig || !this.currentConfig.contacts) {
        return
      }
      this.currentConfig.contacts.splice(index, 1)
    },
    getCompleteContacts (contacts) {
      return (Array.isArray(contacts) ? contacts : [])
        .map((contact) => ({
          type: contact && contact.type ? String(contact.type).trim() : '',
          name: contact && contact.name ? String(contact.name).trim() : '',
          value: contact && contact.value ? String(contact.value).trim() : '',
          notifyTimeRanges: Array.isArray(contact && contact.notifyTimeRanges)
            ? contact.notifyTimeRanges
              .filter((range) => (Array.isArray(range) && range.length === 2))
              .map((range) => ([this.formatTimeValueToHHMM(range[0]), this.formatTimeValueToHHMM(range[1])]))
              .filter((range) => (range[0] && range[1]))
            : []
        }))
        .filter((contact) => (contact.type && contact.name && contact.value))
    },
    getCompleteTerminalCoordinates (terminalCoordinates) {
      return (Array.isArray(terminalCoordinates) ? terminalCoordinates : [])
        .map((terminal) => ({
          name: terminal && terminal.name ? String(terminal.name).trim() : '',
          latitude: terminal && !isNaN(Number(terminal.latitude)) ? Number(terminal.latitude) : null,
          longitude: terminal && !isNaN(Number(terminal.longitude)) ? Number(terminal.longitude) : null,
          radiusMeters: terminal && !isNaN(Number(terminal.radiusMeters)) ? Number(terminal.radiusMeters) : null
        }))
        .filter((terminal) => (terminal.name && terminal.latitude !== null && terminal.longitude !== null && terminal.radiusMeters !== null))
    },
    validateConfig () {
      if (!this.currentConfig || !this.currentConfig.agencyId) {
        alert('Please provide an agency ID')
        return false
      }
      if (!['bus', 'charger'].includes(this.currentConfig.monitorType)) {
        alert('Please choose a monitor type')
        return false
      }
      if (!Number(this.currentConfig.offlineThresholdMinutes) || Number(this.currentConfig.offlineThresholdMinutes) <= 0) {
        alert('Offline threshold minutes must be a positive number')
        return false
      }
      if (this.currentConfig.monitorType === 'charger' && Number(this.currentConfig.offlineThresholdMinutes) < 10) {
        alert('Offline threshold minutes must be at least 10 for EV charger monitors')
        return false
      }
      if (!Number(this.currentConfig.notificationIntervalMinutes) || Number(this.currentConfig.notificationIntervalMinutes) <= 0) {
        alert('Notification interval minutes must be a positive number')
        return false
      }
      if (this.currentConfig.monitorType === 'bus' && (isNaN(Number(this.currentConfig.consecutiveMapApiFailures)) || Number(this.currentConfig.consecutiveMapApiFailures) < 0)) {
        alert('Consecutive map-api failures must be 0 or greater')
        return false
      }
      const completeContacts = this.getCompleteContacts(this.currentConfig.contacts)
      if (!completeContacts.length) {
        alert('Please add at least one complete contact with type, name and value')
        return false
      }
      const validTime = /^([01]\d|2[0-3]):([0-5]\d)$/
      for (const contact of completeContacts) {
        for (const range of (contact.notifyTimeRanges || [])) {
          if (!validTime.test(range[0]) || !validTime.test(range[1])) {
            alert('Each notify time range must use HH:MM format')
            return false
          }
        }
      }
      if (this.currentConfig.monitorType === 'bus') {
        const completeTerminalCoordinates = this.getCompleteTerminalCoordinates(this.currentConfig.terminalCoordinates)
        if (completeTerminalCoordinates.length !== (Array.isArray(this.currentConfig.terminalCoordinates) ? this.currentConfig.terminalCoordinates.length : 0)) {
          alert('Each terminal requires name, latitude, longitude and radius meters')
          return false
        }
        if (completeTerminalCoordinates.some((terminal) => (Number(terminal.radiusMeters) <= 0))) {
          alert('Terminal radius meters must be greater than 0')
          return false
        }
      }

      return true
    },
    async saveConfig () {
      if (!this.validateConfig()) {
        return
      }

      this.apiInProgress = true

      try {
        const contacts = this.getCompleteContacts(this.currentConfig.contacts)
        const isChargerMonitor = this.isChargerMonitor(this.currentConfig)
        const terminalCoordinates = isChargerMonitor ? [] : this.getCompleteTerminalCoordinates(this.currentConfig.terminalCoordinates)
        const consecutiveMapApiFailures = isChargerMonitor ? undefined : Number(this.currentConfig.consecutiveMapApiFailures)

        if (this.currentConfig.id) {
          await updateOfflineNotificationSetting({
            id: this.currentConfig.id,
            monitorType: this.currentConfig.monitorType,
            contacts,
            offlineThresholdMinutes: Number(this.currentConfig.offlineThresholdMinutes),
            notificationIntervalMinutes: Number(this.currentConfig.notificationIntervalMinutes),
            enabled: Boolean(this.currentConfig.enabled),
            consecutiveMapApiFailures,
            terminalCoordinates
          })

          addAlert({
            message: `Successfully updated config for agency: ${this.currentConfig.agencyId}`,
            type: 'success',
            transient: true
          })

          await this.loadConfigs()
          await this.editConfig(this.currentConfig.id)
        } else {
          await addOfflineNotificationSetting({
            agencyId: this.currentConfig.agencyId,
            monitorType: this.currentConfig.monitorType,
            contacts,
            offlineThresholdMinutes: Number(this.currentConfig.offlineThresholdMinutes),
            notificationIntervalMinutes: Number(this.currentConfig.notificationIntervalMinutes),
            enabled: Boolean(this.currentConfig.enabled),
            consecutiveMapApiFailures,
            terminalCoordinates
          })
          addAlert({
            message: `Successfully created config for agency: ${this.currentConfig.agencyId}`,
            type: 'success'
          })
          await this.loadConfigs()
          this.currentConfig = null
          this.originalCurrentConfig = null
        }
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    async deleteConfig (config) {
      if (!config || !config.id) {
        return
      }

      if (config.agencyId === defaultProtectedAgencyId) {
        return alert(`Cannot delete default agency config (${defaultProtectedAgencyId})`)
      }

      if (this.currentConfig && this.currentConfig.id === config.id) {
        return alert('Cannot delete a config that is currently being edited')
      }

      if (!confirm(`Confirm deleting config for agency: ${config.agencyId}?`)) {
        return
      }

      this.apiInProgress = true

      try {
        await deleteOfflineNotificationSetting(config.id)
        await this.loadConfigs()

        addAlert({
          message: `Successfully deleted config for agency: ${config.agencyId}`,
          type: 'success',
          transient: true
        })
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    cancelConfigEdit () {
      if (this.isCurrentConfigEdited && !confirm('Confirm closing this edit, any changes will be lost')) {
        return
      }

      this.currentConfig = null
      this.originalCurrentConfig = null
    }
  }
}
